import classNames from 'classnames';
import React, {ReactNode, useEffect} from 'react';

import {BackendSourceOptions, DiscoveryItemImpressionEvent} from '@udemy/browse-event-tracking';
import {
    generateTrackingId,
    Tracker,
    trackGenericCourseClick,
    TrackImpression,
} from '@udemy/event-tracking';
import {
    CardAuthors,
    CardMediaImage,
    CardTitle,
    CourseProductCard,
    CourseProductCardProps,
    RatingTag,
    Tag,
} from '@udemy/react-product-card-components';
import {CourseCardSkeleton} from '@udemy/react-reveal-components';
import {pxToRem} from '@udemy/styles';

import {getRuntimeConfig} from 'src/pages/api/config.api';
import {getCourseData} from 'src/pages/get-course-data';

import styles from './course-section.module.less';

export interface CourseProps {
    courseInstructor: string;
    link: string;
    title: string;
    args?: {authors: ReactNode; title: ReactNode; tags: ReactNode[]};
    image: string;
    category: string;
    id: number;
    trackingId?: string;
}

interface PropsType {
    category: string;
    key: number;
    id: number;
    page: string;
}

export const CompleteCourseCard = (props: PropsType) => {
    const [coursesData, setCoursesData] = React.useState<CourseProps>();
    const [isLoaded, setIsLoaded] = React.useState<boolean>(false);
    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await getCourseData(props.id);
                setCoursesData({
                    courseInstructor: data.visible_instructors[0].name,
                    link: `${getRuntimeConfig('MONOLITH_URL')}/course/${props.id}/`,
                    title: data.title,
                    args: {
                        authors: (
                            <CardAuthors
                                authors={data.visible_instructors
                                    .map(
                                        //eslint-disable-next-line @typescript-eslint/no-explicit-any
                                        (instructor: {display_name: any}) =>
                                            instructor.display_name,
                                    )
                                    .join(', ')}
                            />
                        ),
                        title: (
                            <CardTitle
                                renderLink={CardTitle.renderAnchorTag({
                                    href: `${getRuntimeConfig('MONOLITH_URL')}/course/${data.id}/`,
                                    target: '_blank',
                                    onClick: () => {
                                        trackGenericCourseClick({
                                            courseId: props.id,
                                            courseTrackingId: data.tracking_id ?? String(props.id),
                                            componentName: `BenesseJPLandingPageCourseCard_${props.id}`,
                                        });
                                    },
                                })}
                                headingLevel="h2"
                            >
                                {data.title}
                            </CardTitle>
                        ),
                        tags: [
                            <RatingTag key="tag-3" rating={data.avg_rating_recent.toFixed(1)} />,
                            <Tag key="tag-4">{data.num_reviews.toLocaleString()} 件の評価</Tag>,
                        ],
                    },
                    image: data.image_304x171,
                    category: props.category,
                    id: data.id,
                    trackingId: data.tracking_id,
                });
                setIsLoaded(true);
            } catch (error) {
                setCoursesData(undefined);
                setIsLoaded(true);
            }
        };
        fetchData();
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.category, props.id]);
    const clientEvent = {
        id: props.id,
        position: 0,
        type: 'course_category',
        trackingId: generateTrackingId(),
        serveTrackingId: String(props.id),
        backendSource: BackendSourceOptions.DISCOVERY,
        uiRegion: props.page,
        badgeFamilies: null,
        relatedSourceId: null,
        relatedSourceType: null,
    };

    const trackImpression = function () {
        Tracker.publishEvent(new DiscoveryItemImpressionEvent(clientEvent));
    };

    return isLoaded ? (
        coursesData ? (
            <TrackImpression trackFunc={trackImpression} key={props.id}>
                <div>
                    <CourseProductCard
                        className={classNames(styles['course-card'])}
                        {...(coursesData.args as CourseProductCardProps)}
                        key={`${props.category}_${props.key}`}
                        layout={'vertical'}
                        mediaSquare={
                            <CardMediaImage
                                layout="wide"
                                src={coursesData.image}
                                src2x={coursesData.image}
                            />
                        }
                        mediaWide={
                            <CardMediaImage
                                layout="wide"
                                src={coursesData.image}
                                src2x={coursesData.image}
                            />
                        }
                    />
                </div>
            </TrackImpression>
        ) : (
            <></>
        )
    ) : (
        <div className={classNames(styles['skeleton-card-container'])}>
            <CourseCardSkeleton
                size="medium"
                style={{
                    width: `${pxToRem(300)}rem`,
                    minWidth: `${pxToRem(300)}rem`,
                    maxWidth: `${pxToRem(300)}rem`,
                }}
                imageStyle={{width: `${pxToRem(300)}rem`, height: `${pxToRem(120)}rem`}}
                lineCount={4}
            />
        </div>
    );
};
